/**
 * Persistent Authentication Service
 * Handles automatic session refresh and "always signed-in" functionality.
 *
 * Multi-device safety:
 * - Prefer the existing session cookie before calling /api/auth/refresh
 * - Never rotate/refresh aggressively on every visibility change
 * - Do not log the user out when refresh fails but the session is still valid
 *   (common when another device refreshed/rotated the shared refresh token)
 */

export type RefreshOptions = {
  /** When true (default for explicit restore), clear auth if refresh fails AND session is dead */
  clearOnFailure?: boolean;
  /** Force a refresh even if a session check recently succeeded */
  force?: boolean;
};

export class PersistentAuthService {
  private static instance: PersistentAuthService;
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private initPromise: Promise<boolean> | null = null;
  private lastSuccessfulAuthAt = 0;
  private lastRefreshAttemptAt = 0;
  private lastTokenRefreshAt = 0;
  private lastVisibilityCheckAt = 0;

  /** Minimum gap between visibility-triggered session checks */
  private static readonly VISIBILITY_CHECK_COOLDOWN_MS = 15 * 1000;
  /** Only proactively hit /api/auth/refresh if session has been idle this long */
  private static readonly PROACTIVE_REFRESH_AFTER_MS = 12 * 60 * 60 * 1000; // 12 hours

  private constructor() {
    this.setupVisibilityListener();
    this.setupStorageListener();
  }

  static getInstance(): PersistentAuthService {
    if (!PersistentAuthService.instance) {
      PersistentAuthService.instance = new PersistentAuthService();
    }
    return PersistentAuthService.instance;
  }

  /**
   * Initialize persistent authentication.
   * Should be called on app startup. Safe to call multiple times — returns same promise.
   */
  async initialize(): Promise<boolean> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize();
    return this.initPromise;
  }

  /** Whether initialize() has settled (success or failure). */
  isReady(): boolean {
    return this.initPromise !== null && !this.isRefreshing;
  }

  /** Promise that resolves when startup initialize completes. */
  whenReady(): Promise<boolean> {
    return this.initialize();
  }

  private async doInitialize(): Promise<boolean> {
    console.log('🔐 Initializing persistent authentication...');

    try {
      // Prefer existing session cookie — avoid unnecessary refresh-token rotation on every load
      const sessionUser = await this.fetchSessionUser();
      if (sessionUser) {
        console.log('✅ Session cookie still valid — skipping refresh on startup');
        this.lastSuccessfulAuthAt = Date.now();
        // Treat startup session as freshly verified so we don't immediately rotate
        this.lastTokenRefreshAt = Date.now();
        this.notifyAuthStateChange(sessionUser);
        this.scheduleNextRefresh();
        return true;
      }

      const success = await this.refreshSession({ clearOnFailure: true, force: true });

      if (success) {
        console.log('✅ Persistent authentication restored via refresh token');
        this.scheduleNextRefresh();
        return true;
      }

      console.log('ℹ️ No valid session to restore');
      return false;
    } catch (error) {
      console.error('❌ Failed to initialize persistent auth:', error);
      return false;
    }
  }

  /**
   * Refresh the current session using refresh token
   */
  async refreshSession(options: RefreshOptions = {}): Promise<boolean> {
    const { clearOnFailure = true, force = false } = options;

    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Throttle non-forced refresh attempts (avoids races across tabs/devices)
    const now = Date.now();
    if (!force && now - this.lastRefreshAttemptAt < 5_000) {
      console.log('⏳ Skipping refresh — recent attempt in flight/cooldown');
      // If we recently authenticated successfully, treat as still logged in
      return this.lastSuccessfulAuthAt > 0 && now - this.lastSuccessfulAuthAt < 24 * 60 * 60 * 1000;
    }

    this.isRefreshing = true;
    this.lastRefreshAttemptAt = now;
    this.refreshPromise = this.performRefresh(clearOnFailure);

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performRefresh(clearOnFailure: boolean): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔄 Session refreshed successfully');

        // Small delay so Set-Cookie is applied before follow-up requests
        await new Promise((resolve) => setTimeout(resolve, 100));
        console.log('✅ Cookie processing delay complete');

        this.lastSuccessfulAuthAt = Date.now();
        this.lastTokenRefreshAt = Date.now();
        this.notifyAuthStateChange(data.user);
        return true;
      }

      console.log('🔒 Session refresh failed:', response.status);

      // CRITICAL: Another device may have rotated the shared refresh token.
      // If the session cookie is still valid, keep the user logged in.
      const sessionUser = await this.fetchSessionUser();
      if (sessionUser) {
        console.log('✅ Session cookie still valid after failed refresh — keeping user logged in');
        this.lastSuccessfulAuthAt = Date.now();
        this.notifyAuthStateChange(sessionUser);
        return true;
      }

      if (clearOnFailure) {
        console.log('🔒 No valid session after refresh failure — clearing auth');
        this.clearAuthState();
      }
      return false;
    } catch (error) {
      console.error('❌ Session refresh error:', error);
      // Network errors must not log the user out
      const sessionUser = await this.fetchSessionUser();
      if (sessionUser) {
        this.lastSuccessfulAuthAt = Date.now();
        this.notifyAuthStateChange(sessionUser);
        return true;
      }
      return false;
    }
  }

  /** GET /api/auth/user — returns user if session cookie is valid, else null */
  private async fetchSessionUser(): Promise<any | null> {
    try {
      const res = await fetch('/api/auth/user', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.id ? data : null;
    } catch {
      return null;
    }
  }

  /**
   * Schedule the next session refresh
   * Refresh every 24 hours to keep session alive
   */
  private scheduleNextRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const refreshInterval = 24 * 60 * 60 * 1000; // 24 hours

    this.refreshTimer = setTimeout(() => {
      console.log('⏰ Scheduled session refresh triggered');
      this.refreshSession({ clearOnFailure: false, force: true }).then((success) => {
        if (success) {
          this.scheduleNextRefresh();
        }
      });
    }, refreshInterval);

    console.log(`⏱️ Next session refresh scheduled in ${refreshInterval / 1000 / 60 / 60} hours`);
  }

  /**
   * On visibility: verify session first. Only refresh-token when session is dead
   * or a long proactive refresh interval has elapsed. Avoids multi-device logout races.
   */
  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;

      const now = Date.now();
      if (now - this.lastVisibilityCheckAt < PersistentAuthService.VISIBILITY_CHECK_COOLDOWN_MS) {
        return;
      }
      this.lastVisibilityCheckAt = now;

      console.log('👁️ Page became visible — verifying session (not forcing token refresh)');
      void this.handleVisibilityReturn();
    });
  }

  private async handleVisibilityReturn(): Promise<void> {
    const sessionUser = await this.fetchSessionUser();
    if (sessionUser) {
      this.lastSuccessfulAuthAt = Date.now();
      this.notifyAuthStateChange(sessionUser);

      const idleSinceRefresh = Date.now() - this.lastTokenRefreshAt;
      const needsProactiveRefresh =
        idleSinceRefresh > PersistentAuthService.PROACTIVE_REFRESH_AFTER_MS;

      if (needsProactiveRefresh) {
        console.log('🔄 Proactive refresh after long idle');
        const ok = await this.refreshSession({ clearOnFailure: false, force: true });
        if (ok) this.scheduleNextRefresh();
      }
      return;
    }

    // Session cookie expired — try refresh token to restore
    console.log('⚠️ Session missing on visibility — attempting refresh token restore');
    const ok = await this.refreshSession({ clearOnFailure: true, force: true });
    if (ok) this.scheduleNextRefresh();
  }

  /**
   * Handle storage events for cross-tab synchronization
   */
  private setupStorageListener(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('storage', (event) => {
      if (event.key === 'auth-logout') {
        console.log('🔄 Logout detected in another tab');
        this.clearAuthState();
      }
    });
  }

  /**
   * Notify the app about auth state changes
   * NOTE: This is for automatic session restoration only - data refresh is NOT triggered here
   * Data refresh is handled separately in the explicit login flow (useAuth.ts loginMutation)
   */
  private notifyAuthStateChange(user: any): void {
    if (typeof window === 'undefined') return;

    window.dispatchEvent(
      new CustomEvent('auth-state-change', {
        detail: { user, authenticated: true },
      })
    );
  }

  /**
   * Clear authentication state
   * @param notifyOtherTabs - Whether to notify other tabs about logout (only true for explicit logout)
   */
  private clearAuthState(notifyOtherTabs: boolean = false): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    this.lastSuccessfulAuthAt = 0;

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('auth-state-change', {
          detail: { user: null, authenticated: false },
        })
      );

      // Only signal other tabs during explicit logout
      if (notifyOtherTabs) {
        console.log('📢 Notifying other tabs about logout');
        localStorage.setItem('auth-logout', Date.now().toString());
        setTimeout(() => localStorage.removeItem('auth-logout'), 1000);
      }
    }
  }

  /**
   * Handle user logout
   */
  async logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthState(true);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.isRefreshing = false;
    this.refreshPromise = null;
  }
}

// Export singleton instance
export const persistentAuth = PersistentAuthService.getInstance();

// Auto-initialize when module is loaded (skip under Vitest — tests drive initialize explicitly)
const isVitest =
  typeof process !== 'undefined' &&
  (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test');

if (typeof window !== 'undefined' && !isVitest) {
  persistentAuth.initialize();
}
